import { useEffect, useMemo, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import type { QualitySample, SessionQuality } from "../api/parse.ts";
import { sessionWindowTitle } from "../sessionTitle.ts";
import { gradeFeed, gradeHeld, initialGradeState, qualityLogLine, type NetworkGradeReading } from "./networkGrade.ts";
import { QualityCharts } from "./qualityChart.tsx";

export type QualitySampleView = QualitySample;
export type SessionQualityView = SessionQuality;

const CAPTURE_SLOW_MS = 40;
const ENCODE_SLOW_MS = 40;
const NETWORK_RTT_MS = 150;
const NETWORK_LOSS = 0.05;
const RELAY_RTT_MS = 80;

export function worstMoment(samples: QualitySampleView[]): string {
  let capture = 0;
  let encode = 0;
  let network = 0;
  let maxCapture = 0;
  let maxEncode = 0;
  let maxRtt = 0;
  let maxLoss = 0;
  let relayRtt = 0;
  for (const sample of samples) {
    if (sample.captureMs >= CAPTURE_SLOW_MS) {
      capture += 1;
      maxCapture = Math.max(maxCapture, sample.captureMs);
    }
    if (sample.encodeMs >= ENCODE_SLOW_MS) {
      encode += 1;
      maxEncode = Math.max(maxEncode, sample.encodeMs);
    }
    const rtt = sample.rttMs;
    const loss = sample.packetLoss;
    const slowNetwork =
      (rtt !== null && rtt >= NETWORK_RTT_MS) ||
      (loss !== null && loss >= NETWORK_LOSS) ||
      (sample.icePath === "relayed" && rtt !== null && rtt >= RELAY_RTT_MS);
    if (slowNetwork) {
      network += 1;
      if (rtt !== null) maxRtt = Math.max(maxRtt, rtt);
      if (loss !== null) maxLoss = Math.max(maxLoss, loss);
      if (sample.icePath === "relayed" && rtt !== null) relayRtt = Math.max(relayRtt, rtt);
    }
  }
  if (capture === 0 && encode === 0 && network === 0) return "No slow patch in this session.";
  if (capture >= encode && capture >= network) {
    return `Slow patch was capture. Capture time reached ${Math.round(maxCapture)} ms.`;
  }
  if (encode >= network) {
    return `Slow patch was encode. Encode time reached ${Math.round(maxEncode)} ms.`;
  }
  if (maxRtt >= NETWORK_RTT_MS) {
    return `Slow patch was network. Round-trip time reached ${Math.round(maxRtt)} ms.`;
  }
  if (maxLoss >= NETWORK_LOSS) {
    return `Slow patch was network. Packet loss reached ${percent(maxLoss)}.`;
  }
  return `Slow patch was network. The path was relayed and round-trip time reached ${Math.round(relayRtt)} ms.`;
}

function networkLabel(sample: QualitySampleView): string {
  if (sample.rttMs === null) return "not measured";
  return `${Math.round(sample.rttMs)} ms rtt`;
}

function percent(loss: number): string {
  const rounded = Math.round(loss * 1000) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

export function offerPickup(offerLagMs: number): string {
  return `Offer pickup ${(offerLagMs / 1000).toFixed(1)} s.`;
}

type Popout = { window: Window; root: Root; deviceName: string };
type QualityDevice = { deviceId?: string; deviceName?: string };

const popouts = new Map<string, Popout>();
const DEVICE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function qualityPopoutName(deviceId: string): string {
  if (!DEVICE_UUID.test(deviceId)) return "wylo-rc-session-quality-unscoped";
  return `wylo-rc-session-quality-${deviceId}`;
}

const POPOUT_STYLE =
  "body{margin:16px;min-width:480px;font:16px 'Segoe UI',sans-serif;color:#241f1a;background:#f6f1e7}h2{margin:0 0 8px;font-size:16px}.quality-summary,.quality-lag,.quality-why{margin:4px 0}ul{padding-left:18px}svg{display:block;width:100%}";
const SIGN_OUT_KEY = "wylo-rc-quality-sign-out";

function popoutKey(device: QualityDevice | undefined): string {
  return device?.deviceId && DEVICE_UUID.test(device.deviceId) ? device.deviceId : "unscoped";
}

export function openQualityPopout(
  quality: SessionQualityView | null,
  device?: QualityDevice,
  reading?: NetworkGradeReading,
): "opened" | "blocked" {
  const key = popoutKey(device);
  const deviceName = device?.deviceName ?? "";
  const current = popouts.get(key);
  if (current?.window.closed) closeQualityPopout(key);
  const live = popouts.get(key);
  if (live) {
    try {
      live.deviceName = deviceName;
      paint(live, quality, reading);
      live.window.focus();
      return "opened";
    } catch {
      closeQualityPopout(key);
      return "blocked";
    }
  }
  let child: Window | null = null;
  const windowName = qualityPopoutName(key === "unscoped" ? "" : key);
  try {
    child = window.open("", windowName, "popup,width=760,height=720");
  } catch {
    return "blocked";
  }
  if (!child) return "blocked";
  try {
    child.opener = null;
    const doc = child.document;
    const style = doc.createElement("style");
    style.textContent = POPOUT_STYLE;
    doc.head.append(style);
    const mount = doc.createElement("div");
    mount.id = "quality-root";
    doc.title = sessionWindowTitle(deviceName, "quality");
    doc.body.replaceChildren(mount);
    const root = createRoot(mount);
    const popout: Popout = { window: child, root, deviceName };
    popouts.set(key, popout);
    paint(popout, quality, reading);
  } catch {
    if (popouts.get(key)?.window === child) closeQualityPopout(key);
    else {
      try {
        child.close();
      } catch {
        /* the child is already gone */
      }
    }
    return "blocked";
  }
  return "opened";
}

export function syncQualityPopout(
  quality: SessionQualityView | null,
  device?: QualityDevice,
  reading?: NetworkGradeReading,
): void {
  const current = popouts.get(popoutKey(device));
  if (!current || current.window.closed) return;
  try {
    current.deviceName = device?.deviceName ?? current.deviceName;
    paint(current, quality, reading);
  } catch {
    closeQualityPopout(popoutKey(device));
  }
}

export function closeQualityPopout(deviceId?: string): void {
  if (deviceId && DEVICE_UUID.test(deviceId)) {
    closeOne(deviceId);
    return;
  }
  if (deviceId === "unscoped") {
    closeOne("unscoped");
    return;
  }
  for (const key of [...popouts.keys()]) closeOne(key);
}

function closeOne(key: string): void {
  const current = popouts.get(key);
  popouts.delete(key);
  if (!current) return;
  queueMicrotask(() => {
    try {
      current.root.unmount();
    } catch {
      /* the child document is already gone */
    }
  });
  try {
    if (!current.window.closed) current.window.close();
  } catch {
    /* the child is already gone */
  }
}

export function releaseQualityOnSignOut(): void {
  closeQualityPopout();
  try {
    localStorage.setItem(SIGN_OUT_KEY, String(Date.now()));
    localStorage.removeItem(SIGN_OUT_KEY);
  } catch {
    /* storage is unavailable */
  }
}

export function watchQualitySignOut(onSignOut: () => void): () => void {
  const hear = (event: StorageEvent) => {
    if (event.key !== SIGN_OUT_KEY) return;
    closeQualityPopout();
    onSignOut();
  };
  window.addEventListener("storage", hear);
  return () => window.removeEventListener("storage", hear);
}

function paint(target: Popout, quality: SessionQualityView | null, reading?: NetworkGradeReading) {
  try {
    target.window.document.title = sessionWindowTitle(target.deviceName, "quality");
  } catch {
    /* the child document is already gone */
  }
  target.root.render(<QualityGraph quality={quality} reading={reading} />);
}

export function readQuality(
  quality: SessionQualityView | null,
  arrivals?: readonly number[],
  now = Date.now(),
): NetworkGradeReading {
  const samples = quality?.samples ?? null;
  const stamps = arrivals ?? (samples && samples.length > 0 ? [now] : []);
  return gradeFeed(samples, stamps, now);
}

function useHeldReading(
  quality: SessionQualityView | null,
  arrivals: readonly number[] | undefined,
  now: number | undefined,
  override: NetworkGradeReading | undefined,
): NetworkGradeReading {
  const held = useRef(initialGradeState());
  const samples = quality?.samples ?? [];
  const clock = now ?? Date.now();
  const newestMs = samples.length > 0 ? Date.parse(samples[samples.length - 1].sampledAt) : Number.NaN;
  return useMemo(() => {
    if (override) return override;
    if (samples.length === 0 || (Number.isFinite(newestMs) && Number.isFinite(held.current.lastStamp) && newestMs < held.current.lastStamp)) {
      held.current = initialGradeState();
    }
    const stamps = arrivals ?? (samples.length > 0 ? [clock] : []);
    const stepped = gradeHeld(held.current, samples, stamps, clock);
    held.current = stepped.state;
    return stepped.reading;
  }, [override, quality, arrivals, clock]);
}

export function SessionQualityControls(props: {
  quality: SessionQualityView | null;
  deviceId?: string;
  deviceName?: string;
  arrivals?: readonly number[];
  now?: number;
  reading?: NetworkGradeReading;
}) {
  const reading = useHeldReading(props.quality, props.arrivals, props.now, props.reading);
  const logLine = qualityLogLine(reading);
  const device = { deviceId: props.deviceId, deviceName: props.deviceName };
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    syncQualityPopout(props.quality, device, reading);
  }, [props.quality, props.deviceId, props.deviceName, reading]);
  useEffect(() => {
    console.info(logLine);
  }, [logLine]);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          const result = openQualityPopout(props.quality, device, reading);
          setBlocked(result === "blocked");
        }}
      >
        Session quality
      </button>
      <span className={`network-grade ${reading.grade}`} title={reading.tooltip} aria-label={reading.tooltip}>
        {reading.label}
      </span>
      {blocked ? <span role="status">Allow pop-ups for this site to open session quality.</span> : null}
    </>
  );
}

export function QualityGraph(props: { quality: SessionQualityView | null; arrivals?: readonly number[]; now?: number; reading?: NetworkGradeReading }) {
  const samples = props.quality?.samples.slice(-60) ?? [];
  const latest = samples.slice(-8);
  const reading = useHeldReading(props.quality, props.arrivals, props.now, props.reading);
  return (
    <section className="quality-graph" aria-label="Session quality">
      <h2>Session quality</h2>
      <p className="quality-why">{reading.why ?? reading.label}</p>
      {props.quality ? <p className="quality-summary">{props.quality.summary}</p> : <p>Waiting for the first sample.</p>}
      {props.quality?.offerLagMs === null || props.quality?.offerLagMs === undefined ? null : (
        <p className="quality-lag">{offerPickup(props.quality.offerLagMs)}</p>
      )}
      <QualityCharts samples={samples} />
      <ul>
        {latest.map((sample, index) => (
          <li key={`${sample.sampledAt}-${index}`}>
            {sample.fps.toFixed(1)} fps, {Math.round(sample.bitrate / 1000)} kbps, {networkLabel(sample)}, {sample.icePath}, capture{" "}
            {Math.round(sample.captureMs)} ms, encode {Math.round(sample.encodeMs)} ms
          </li>
        ))}
      </ul>
    </section>
  );
}

const PROBE: SessionQualityView = {
  offerLagMs: 10600,
  summary: "Slow patch was capture. Capture time reached 80 ms.",
  samples: [
    {
      sampledAt: "2026-10-24T04:00:00.000Z",
      fps: 10,
      bitrate: 2_000_000,
      rttMs: 320,
      jitterMs: 5,
      packetLoss: 0,
      captureMs: 8,
      encodeMs: 6,
      icePath: "direct",
    },
    {
      sampledAt: "2026-10-24T04:00:02.000Z",
      fps: 10,
      bitrate: 1_800_000,
      rttMs: 320,
      jitterMs: 5,
      packetLoss: 0,
      captureMs: 12,
      encodeMs: 7,
      icePath: "direct",
    },
    {
      sampledAt: "2026-10-24T04:00:04.000Z",
      fps: 10,
      bitrate: 1_700_000,
      rttMs: 320,
      jitterMs: 5,
      packetLoss: 0,
      captureMs: 10,
      encodeMs: 6,
      icePath: "direct",
    },
    {
      sampledAt: "2026-10-24T04:00:06.000Z",
      fps: 10,
      bitrate: 1_600_000,
      rttMs: 320,
      jitterMs: 5,
      packetLoss: 0,
      captureMs: 9,
      encodeMs: 6,
      icePath: "direct",
    },
    {
      sampledAt: "2026-10-24T04:00:08.000Z",
      fps: 10,
      bitrate: 1_500_000,
      rttMs: 320,
      jitterMs: 5,
      packetLoss: 0,
      captureMs: 9,
      encodeMs: 6,
      icePath: "direct",
    },
  ],
};

export function QualityProbe() {
  const sampledAt = new Date().toISOString();
  const quality: SessionQualityView = {
    ...PROBE,
    samples: PROBE.samples.map((sample, index) => ({
      ...sample,
      sampledAt: new Date(Date.parse(sampledAt) - (PROBE.samples.length - 1 - index) * 2000).toISOString(),
    })),
  };
  return (
    <section className="panel">
      <h2>Remote Control session</h2>
      <div className="view-switch">
        <SessionQualityControls quality={quality} />
      </div>
      <QualityGraph quality={quality} />
    </section>
  );
}
